import { useEffect, useRef, useState } from "react";
import { IoSaveOutline } from "react-icons/io5";
import { uploadMedia } from "../../../api/mediaApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import { useNotify } from "../../../components/ui/NotificationProvider";
import FieldInputRenderer from "../fields/FieldInputRenderer";
import {
  ARTICLE_FIELD_DEFS,
  EMPTY_ARTICLE_FORM,
  slugifyTitle,
} from "./articleFieldUtils";

const PRIMARY_FIELDS = ["slug", "category", "title", "desc", "date"];
const AUTHOR_FIELDS = ["author", "role", "image", "avatar"];

export default function ArticleFormModal({
  open,
  sectionId,
  initialForm,
  editingArticleKey = null,
  isEdit = false,
  busy,
  onClose,
  onSubmit,
  onSaveField,
}) {
  const notify = useNotify();
  const [form, setForm] = useState(EMPTY_ARTICLE_FORM);
  const [uploadBusyKey, setUploadBusyKey] = useState(null);
  const initialSnapshotRef = useRef(EMPTY_ARTICLE_FORM);

  useEffect(() => {
    if (!open) return;
    const snapshot = initialForm
      ? { ...EMPTY_ARTICLE_FORM, ...initialForm }
      : { ...EMPTY_ARTICLE_FORM };
    initialSnapshotRef.current = snapshot;
    setForm(snapshot);
    // Only reset when opening the modal or switching to a different article.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingArticleKey]);

  if (!open) return null;

  const hasChanges = ARTICLE_FIELD_DEFS.some(
    (def) => (form[def.suffix] ?? "") !== (initialSnapshotRef.current[def.suffix] ?? "")
  );

  function setField(suffix, value) {
    setForm((prev) => ({ ...prev, [suffix]: value }));
  }

  function handleTitleBlur() {
    if (isEdit) return;
    if (!form.slug.trim() && form.title.trim()) {
      setForm((prev) => ({ ...prev, slug: slugifyTitle(prev.title) }));
    }
  }

  async function handleUpload(suffix, file) {
    setUploadBusyKey(suffix);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mediaType", "image");
      formData.append("groupKey", "articles");
      formData.append("altText", file.name);
      formData.append("isPrimary", "false");
      formData.append("sortOrder", "0");

      const uploaded = await uploadMedia(sectionId, formData);
      const url = uploaded?.url ?? uploaded?.Url ?? "";
      if (!url) throw new Error("Upload did not return a URL");

      setField(suffix, url);

      if (isEdit && onSaveField) {
        await onSaveField(suffix, url);
        initialSnapshotRef.current = {
          ...initialSnapshotRef.current,
          [suffix]: url,
        };
      }

      notify.success(isEdit ? "Image saved" : "Image uploaded");
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setUploadBusyKey(null);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit({
      ...form,
      slug: form.slug.trim().toLowerCase(),
    });
  }

  function renderField(suffix) {
    const def = ARTICLE_FIELD_DEFS.find((d) => d.suffix === suffix);
    if (!def) return null;

    const isMedia = def.fieldType === "image";

    return (
      <FieldInputRenderer
        key={suffix}
        field={{
          key: suffix,
          label: def.label,
          fieldType: def.fieldType,
          isRequired: suffix === "title" || suffix === "slug",
        }}
        value={form[suffix] ?? ""}
        onChange={(next) => setField(suffix, next)}
        onUpload={isMedia ? (file) => handleUpload(suffix, file) : undefined}
        uploadBusy={uploadBusyKey === suffix}
      />
    );
  }

  const isEditMode = isEdit;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
      />
      <form
        onSubmit={handleSubmit}
        className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
      >
        <div className="border-b border-gray-100 px-6 py-5">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEditMode ? "Edit article" : "New article"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {isEditMode
              ? "Change any field you need — only modified values are saved."
              : "Fill in the fields and create the article."}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Article
              </p>
              {PRIMARY_FIELDS.map((suffix) => (
                <div
                  key={suffix}
                  onBlur={suffix === "title" ? handleTitleBlur : undefined}
                >
                  {renderField(suffix)}
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Author & images
              </p>
              {AUTHOR_FIELDS.map((suffix) => renderField(suffix))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              busy ||
              (isEditMode
                ? !hasChanges
                : !form.title.trim() || !form.slug.trim())
            }
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            <IoSaveOutline className="h-4 w-4" />
            {busy ? "Saving…" : isEditMode ? "Save article" : "Create article"}
          </button>
        </div>
      </form>
    </div>
  );
}
